import { useEffect } from 'react';

const ProductRankBadge = ({
  rank = 1,
  year = '2026',
  month = '1月',
  categoryName = '', // 分類名稱
  size = 'medium', // 尺寸: "small" | "medium" | "large" (預設 "medium")
  width: customWidth, // 自訂寬度 (會覆蓋 size 設定)
}) => {
  const getBadgeConfig = () => {
    switch (rank) {
      case 1:
        return { svg: '/svgs/rank-badge/1.svg', colorClass: 'text-[#E9A302]' };
      case 2:
        return { svg: '/svgs/rank-badge/2.svg', colorClass: 'text-[#6E7F86]' };
      case 3:
        return { svg: '/svgs/rank-badge/3.svg', colorClass: 'text-[#DF9963]' };
      default:
        return { svg: '/svgs/rank-badge/4.svg', colorClass: 'text-[#F16B7C]' };
    }
  };

  const getSizeConfig = () => {
    const configs = {
      small: {
        width: 34,
        height: 34,
        sorraSize: 2,
        sorraLetterSpacing: '0.05em',
        dateSize: 2.5,
        rankNumSize: 12,
        rankTextSize: 2.5,
        rankTop: 1,
        starTop: -0.3,
        ribbonTop: 0.5,
        ribbonFontSize: 10,
      },
      medium: {
        width: 69,
        height: 69,
        sorraSize: 4,
        sorraLetterSpacing: '0.12em',
        dateSize: 5,
        rankNumSize: 25,
        rankTextSize: 6,
        rankTop: 2,
        starTop: -0.4,
        ribbonTop: 1.5,
        ribbonFontSize: 11,
      },
      large: {
        width: 124,
        height: 124,
        sorraSize: 6,
        sorraLetterSpacing: '0.15em',
        dateSize: 7,
        rankNumSize: 46,
        rankTextSize: 10,
        rankTop: 2,
        starTop: 0.2,
        ribbonTop: 1.5,
        ribbonFontSize: 13,
      },
    };

    const baseConfig = configs[size] || configs.medium;
    // 確保 customWidth 是數字
    const parsedCustomWidth = customWidth ? Number(customWidth) : null;
    const width = parsedCustomWidth || baseConfig.width;
    const height = parsedCustomWidth || baseConfig.height;

    // 如果有自訂寬度，按比例縮放
    if (parsedCustomWidth) {
      const scale = parsedCustomWidth / configs[size].width;
      return {
        width,
        height,
        sorraSize: baseConfig.sorraSize * scale,
        sorraLetterSpacing: baseConfig.sorraLetterSpacing,
        dateSize: baseConfig.dateSize * scale,
        rankNumSize: baseConfig.rankNumSize * scale,
        rankTextSize: baseConfig.rankTextSize * scale,
        rankTop: baseConfig.rankTop * scale,
        starTop: baseConfig.starTop * scale,
        ribbonTop: baseConfig.ribbonTop * scale,
        // ribbonFontSize 最小為 8
        ribbonFontSize: Math.max(8, baseConfig.ribbonFontSize * scale),
      };
    }

    return {
      width,
      height,
      sorraSize: baseConfig.sorraSize,
      sorraLetterSpacing: baseConfig.sorraLetterSpacing,
      dateSize: baseConfig.dateSize,
      rankNumSize: baseConfig.rankNumSize,
      rankTextSize: baseConfig.rankTextSize,
      rankTop: baseConfig.rankTop,
      starTop: baseConfig.starTop,
      ribbonTop: baseConfig.ribbonTop,
      ribbonFontSize: baseConfig.ribbonFontSize,
    };
  };

  const config = getBadgeConfig();
  const sizeConfig = getSizeConfig();

  const truncatedCategoryName =
    categoryName.length > 15 ? categoryName.slice(0, 14) + '...' : categoryName;



  return (
    <div
      className="font-jinxuanlatte relative inline-block shrink-0"
      style={{ width: sizeConfig.width, height: sizeConfig.height }}
    >
      {/* 背景 SVG */}
      <img
        src={config.svg}
        alt={`第${rank}名`}
        className="h-full w-full object-contain"
        draggable={false}
      />

      {/* SORRA✦ 標題 */}
      <div
        className={`font-base font-jinxuanlatte absolute left-1/2 top-[19%] -translate-x-1/2 whitespace-nowrap text-center ${config.colorClass}`}
        style={{
          fontSize: sizeConfig.sorraSize,
          letterSpacing: '0.15em',
        }}
      >
        SORRA
        <span
          className="ml-px inline-block scale-x-[0.85] scale-y-125"
          style={{
            fontSize: sizeConfig.sorraSize * 0.9,
            verticalAlign: 'middle',
            position: 'relative',
            top: sizeConfig.starTop,
          }}
        >
          ✦
        </span>
      </div>

      {/* 年月 */}
      <div
        className={`font-jinxuanlatte absolute left-1/2 top-[26%] -translate-x-1/2 whitespace-nowrap text-center font-bold ${config.colorClass}`}
        style={{
          fontSize: sizeConfig.dateSize,
        }}
      >
        {year}年{month}
      </div>

      {/* 排名數字 */}
      <div
        className={`absolute left-1/2 top-[49%] flex -translate-x-1/2 -translate-y-1/2 items-end justify-center ${config.colorClass}`}
      >
        <span
          className="font-jinxuanlatte"
          style={{
            fontSize: sizeConfig.rankTextSize,
            marginBottom: sizeConfig.rankNumSize * 0.15,
            marginLeft: 2,
          }}
        >
          第
        </span>
        <span
          className="font-rubik leading-none"
          style={{
            fontSize: sizeConfig.rankNumSize,
            position: 'relative',
            top: sizeConfig.rankTop,
            marginRight: 2,
          }}
        >
          {rank}
        </span>
        <span
          className="font-jinxuanlatte"
          style={{
            fontSize: sizeConfig.rankTextSize,
            marginBottom: sizeConfig.rankNumSize * 0.15,
            marginRight: 2,
          }}
        >
          位
        </span>
      </div>

      {/* 分類名稱 */}
      {categoryName && (
        <svg
          className="absolute left-0 h-full w-full"
          style={{ top: sizeConfig.ribbonTop }}
          viewBox="0 0 180 180"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <path
              id={`ribbonCurve-${rank}`}
              d="M 30 146 Q 90 138 150 146"
              fill="none"
            />
          </defs>
          <text>
            <textPath
              href={`#ribbonCurve-${rank}`}
              startOffset="50%"
              textAnchor="middle"
              className="jf-jinxuanlatte font-bold"
              style={{
                fontSize:
                  categoryName.length > 10
                    ? sizeConfig.ribbonFontSize * 0.8
                    : sizeConfig.ribbonFontSize,
              }}
            >
              {truncatedCategoryName}
            </textPath>
          </text>
        </svg>
      )}
    </div>
  );
};

export default ProductRankBadge;
